import type {
	GridCustomCellProps,
	GridCustomHeaderCellProps,
} from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { edit_05, trash_01 } from "@tql/ui-components-react/untitled/icons";

import { ActionCell } from "~/shared/components/trax-grid/action-cell/action-cell.tsx";
import { BooleanCell } from "~/shared/components/trax-grid/boolean-cell/boolean-cell.tsx";
import { DefaultFlagCell } from "~/shared/components/trax-grid/default-flag-cell/default-flag-cell.tsx";
import { DimensionsCell } from "~/shared/components/trax-grid/dimensions-cell/dimensions-cell.tsx";
import { DropDownCell } from "~/shared/components/trax-grid/drop-down-cell/drop-down-cell.tsx";
import { EditConfirmationCell } from "~/shared/components/trax-grid/edit-confirmation-cell/edit-confirmation-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import { SelectCell } from "~/shared/components/trax-grid/select-cell/select-cell.tsx";
import { SelectCellHeader } from "~/shared/components/trax-grid/select-cell-header/select-cell-header.tsx";
import type { Commodity } from "~/shared/schemas/commodity-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import type { CommoditiesColumnsConfig } from "../../hooks/commodities-columns-config.ts";
import type { CommodityWithDefault } from "../../hooks/use-commodities-data.ts";

export const getColumnConfig = (
	config: CommoditiesColumnsConfig,
): BoardColumn[] => {
	const { state, data, handlers } = config;
	const {
		isEditMode,
		isLoading,
		edit,
		select,
		allDataSelected,
		showSelection,
		isModal,
	} = state;
	const { freightClassData, unitTypes } = data;
	const {
		selection: { handleCellSelect, handleHeaderCellSelect, setAllDataSelected },
		actions: { handleRemoveFromList, handleAddAndClose, onToggleDefault },
		editing: { enterEdit, handleCancelEdit, handleSaveEdit },
	} = handlers;

	const columns: BoardColumn[] = [
		{
			field: "description",
			title: "Description",
			width: isModal ? 150 : 285,
			editor: "text",
			filterable: true,
			show: true,
			className: "line-wrap",
		},
		{
			field: "quantity",
			title: "Qty",
			width: 75,
			editor: "numeric",
			filterable: true,
			show: true,
		},
		{
			field: "freightClass",
			title: "Class",
			width: 75,
			filterable: true,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<DropDownCell
							{...props}
							data={freightClassData ?? []}
							dataItemKey={"id"}
							edit={edit}
							nestedPropertyName={"code"}
							textField={"code"}
							valueField={"id"}
						/>
					),
			},
		},
		{
			field: "unitType",
			title: "Units",
			width: 75,
			filterable: true,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<DropDownCell
							{...props}
							data={unitTypes ?? []}
							dataItemKey={"id"}
							edit={edit}
							nestedPropertyName={"name"}
							textField={"name"}
							valueField={"id"}
						/>
					),
			},
		},
		{
			field: "weight",
			title: "Weight (lbs.)",
			width: 100,
			editor: "numeric",
			filterable: true,
			show: true,
		},
		{
			field: "dimensions",
			title: "Dimensions",
			width: isEditMode ? 200 : 125,
			filterable: true,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<DimensionsCell {...props} edit={edit} />
					),
			},
		},
		{
			field: "nmfc",
			title: "NMFC Code",
			width: 95,
			filterable: true,
			show: true,
		},
		{
			field: "isStackable",
			title: "Stackable",
			width: 75,
			filterable: true,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<BooleanCell {...props} edit={edit} />
					),
			},
		},
		{
			field: "isHazmat",
			title: "Hazmat",
			width: 75,
			filterable: true,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<BooleanCell {...props} edit={edit} />
					),
			},
		},
		{
			field: "isDefault",
			title: "Default",
			width: 75,
			filterable: true,
			show: true,
			reorderable: false,
			cells: {
				data: (props: GridCustomCellProps) => {
					if (isLoading) {
						return <LoaderCell {...props} />;
					}
					const dataItem = props.dataItem as CommodityWithDefault;
					return (
						<DefaultFlagCell
							{...props}
							isDefault={dataItem.isDefault}
							onToggleDefault={(isDefault) =>
								onToggleDefault(dataItem, isDefault)
							}
						/>
					);
				},
			},
		},
	];

	if (showSelection) {
		columns.unshift({
			field: "",
			title: "Select",
			width: 30,
			filterable: false,
			show: true,
			cells: {
				data: (props: GridCustomCellProps) =>
					isLoading ? (
						<LoaderCell {...props} />
					) : (
						<SelectCell
							{...props}
							onSelectChange={handleCellSelect}
							select={select}
						/>
					),
				headerCell: (props: GridCustomHeaderCellProps) => (
					<SelectCellHeader
						{...props}
						allDataSelected={allDataSelected}
						onSelectChange={handleHeaderCellSelect}
						setAllDataSelected={setAllDataSelected}
					/>
				),
			},
		});
	}

	let actionsWidth = 100;
	if (isModal) {
		actionsWidth = isEditMode ? 100 : 60;
	}

	columns.push({
		field: "",
		title: "Actions",
		width: actionsWidth,
		filterable: false,
		show: true,
		reorderable: false,
		cells: {
			data: (props: GridCustomCellProps) => {
				const dataItem = props.dataItem as Commodity;
				if (isLoading) {
					return <LoaderCell {...props} />;
				}

				if (isModal) {
					return (
						<ActionCell<Commodity>
							{...props}
							onAddAndClose={handleAddAndClose}
							onEdit={enterEdit}
							onRemoveFromList={handleRemoveFromList}
						/>
					);
				}

				return (
					<GridTd tdProps={props.tdProps}>
						<div className={"commodity-row-actions"}>
							<Button
								data-testid={"commodities-row-edit-button"}
								onClick={() => enterEdit(dataItem)}
								svgIcon={edit_05}
								variant={"tertiary"}
							/>
							<Button
								data-testid={"commodities-row-delete-button"}
								onClick={() => handleRemoveFromList(dataItem)}
								svgIcon={trash_01}
								variant={"dangerTertiary"}
							/>
						</div>
					</GridTd>
				);
			},
			edit: {
				text: (props: GridCustomCellProps) => (
					<EditConfirmationCell<Commodity>
						{...props}
						cancel={handleCancelEdit}
						save={handleSaveEdit}
					/>
				),
			},
		},
	});

	return columns;
};
